import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Layers, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { isValidFileType, getFileInputAccept, getFileTypeInfo } from '../utils/fileUpload';

interface LifecycleDataUploadProps {
    databaseId?: number | string | null;
    onUploadSuccess?: () => void;
}

interface UploadResult {
    message: string;
    created?: number;
    updated?: number;
    total_rows?: number;
    errors_count?: number;
    errors?: string[];
    additional_errors?: number;
}

interface UploadResultModalProps {
    isOpen: boolean;
    onClose: () => void;
    result: UploadResult;
}

const UploadResultModal: React.FC<UploadResultModalProps> = ({ isOpen, onClose, result }) => {
    if (!isOpen) return null;

    const hasErrors = (result.errors_count || 0) > 0;
    const hasWarnings = result.errors && result.errors.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto w-full">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100' : 'bg-purple-100'}`}>
                            {hasErrors ? (
                                <AlertTriangle className={`${hasErrors ? 'text-amber-600' : 'text-purple-600'}`} size={20} />
                            ) : (
                                <CheckCircle className="text-purple-600" size={20} />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Résultat de l'import des cycles de vie
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    {/* Summary Statistics */}
                    <div className={`p-4 rounded-lg border ${hasErrors ? 'bg-amber-50 border-amber-200' : 'bg-purple-50 border-purple-200'}`}>
                        <h4 className={`font-medium mb-3 ${hasErrors ? 'text-amber-900' : 'text-purple-900'}`}>
                            {hasErrors ? 'Import terminé avec des erreurs' : 'Import terminé avec succès'}
                        </h4>
                        {(result.created !== undefined || result.updated !== undefined) && (
                            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                                {result.created !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                        <span className="font-medium">Entrées créées:</span> {result.created}
                                    </div>
                                )}
                                {result.updated !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                        <span className="font-medium">Entrées mises à jour:</span> {result.updated}
                                    </div>
                                )}
                                {result.total_rows !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                        <span className="font-medium">Total lignes:</span> {result.total_rows}
                                    </div>
                                )}
                                {result.errors_count !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-purple-800'}>
                                        <span className="font-medium">Erreurs:</span> {result.errors_count}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className={`p-2 bg-white bg-opacity-50 rounded border text-xs ${hasErrors ? 'text-amber-800' : 'text-purple-800'}`}>
                            <strong>Résultat:</strong> {result.message}
                        </div>
                    </div>

                    {/* Error Details */}
                    {hasWarnings && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                Détails des erreurs
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {result.errors!.map((error, index) => (
                                    <div key={index} className="text-sm text-red-800 p-2 bg-white bg-opacity-50 rounded border">
                                        {error}
                                    </div>
                                ))}
                            </div>
                            
                            {result.additional_errors && result.additional_errors > 0 && (
                                <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                    <strong>Note:</strong> {result.additional_errors} erreur(s) supplémentaire(s) non affichée(s). 
                                    Consultez les logs du serveur pour la liste complète.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Success Message */}
                    {!hasErrors && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800 text-sm">
                                🎉 Tous les cycles de vie ont été importés avec succès ! 
                                Vous pouvez maintenant utiliser ces informations pour analyser l'évolution de vos produits.
                            </p>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            hasErrors 
                                ? 'bg-amber-600 text-white hover:bg-amber-700' 
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

interface FormatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FormatModal: React.FC<FormatModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-3xl mx-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Format Fichier Cycles de Vie</h3>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Fichiers acceptés</h4>
                        <ul className="text-sm text-gray-700 space-y-1 mb-4">
                            <li><strong>Formats:</strong> {getFileTypeInfo().formats}</li>
                            <li><strong>Encodages CSV:</strong> utf-8, latin1, cp1252 (détection automatique)</li>
                            <li><strong>Délimiteurs CSV:</strong> ; , tabulation, | (détection automatique)</li>
                            <li><strong>Excel multi-feuilles:</strong> seule la première feuille est lue</li>
                            <li><strong>Taille conseillée:</strong> ≤ 50 Mo</li>
                        </ul>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Colonnes requises:</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>item_code:</strong> Identifiant du produit</li>
                            <li><strong>Life Cycle:</strong> Statut actuel du produit</li>
                            <li><strong>Date de changement:</strong> Date du dernier changement (YYYY-MM-DD)</li>
                        </ul>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Colonnes optionnelles (historique):</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>Life Cycle - 1 à Life Cycle - 4:</strong> Statuts historiques</li>
                            <li><strong>Date de changement - 1 à Date de changement - 4:</strong> Dates des changements historiques</li>
                        </ul>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Statuts valides:</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                            <span>• Lancement</span>
                            <span>• Croissance</span>
                            <span>• Maturité</span>
                            <span>• Déclin</span>
                            <span>• Arrêt</span>
                        </div>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Correspondances acceptées:</h4>
                        <div className="text-sm text-gray-700 space-y-1">
                            <p>• "Nouveauté" → Lancement</p>
                            <p>• "Reconduit" → Croissance</p>
                            <p>• "Permanent" → Maturité</p>
                            <p>• "A Epuisement", "Destockage", "Fin de vie" → Déclin</p>
                        </div>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Exemple:</h4>
                        <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto">
                            item_code,Life Cycle,Date de changement,Life Cycle - 1,Date de changement - 1<br/>
                            10160,Nouveauté,2024-01-01,Reconduit,2023-12-01<br/>
                            10161,A Epuisement,2024-02-15,Destockage,2024-01-15
                        </div>
                    </div>
                    
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                        <p className="text-sm text-purple-800">
                            <strong>Note:</strong> Les statuts historiques sont optionnels mais doivent être fournis par ordre chronologique. 
                            Les dates doivent être au format YYYY-MM-DD.
                        </p>
                    </div>
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        Compris
                    </button>
                </div>
            </div>
        </div>
    );
};

const LifecycleDataUpload: React.FC<LifecycleDataUploadProps> = ({ databaseId, onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showFormatModal, setShowFormatModal] = useState<boolean>(false);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const [showResultModal, setShowResultModal] = useState<boolean>(false);
    const { activeDatabase } = useDatabaseContext();
    const inputRef = useRef<HTMLInputElement>(null);

    // Find the target database ID - prioritize databaseId prop over activeDatabase
    const targetDatabaseId = databaseId || activeDatabase?.id || null;

    // Use shared file type validation

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];
            if (isValidFileType(selectedFile)) {
                setFile(selectedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (isValidFileType(droppedFile)) {
                setFile(droppedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const removeFile = () => {
        setFile(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setError('');
        setUploadResult(null);
        setShowResultModal(false);
    };

    const handleSubmitWithConfirmation = () => {
        if (!file || !targetDatabaseId) {
            setError('Veuillez sélectionner un fichier');
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleCloseResultModal = () => {
        setShowResultModal(false);
        setUploadResult(null);
    };

    const handleConfirmedSubmit = async () => {
        if (!file || !targetDatabaseId) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        setError('');
        setShowConfirmDialog(false);
        setUploadResult(null);
        setShowResultModal(false);

        try {
            const response = await api.post(API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.UPLOAD(String(targetDatabaseId)), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Assuming the API returns a result object, or create one from the response
            setUploadResult(response.data || { message: 'Données de cycles de vie importées avec succès !' });
            setShowResultModal(true);
            setFile(null);
            
            // Call success callback if provided
            onUploadSuccess?.();
            
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            
        } catch (err: any) {
            setError(err.response?.data?.error || 'Une erreur est survenue lors de l\'import');
        } finally {
            setLoading(false);
        }
    };

    if (!targetDatabaseId) {
        return (
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                    Aucune base de données sélectionnée. Veuillez sélectionner une base de données avant d'importer des cycles de vie.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-xl">
                        <Layers className="text-purple-600" size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Cycles de Vie
                        </h2>
                        <p className="text-gray-600 text-sm">
                            Importez les statuts et cycles de vie pour {activeDatabase?.name || 'votre base'}
                        </p>
                    </div>
                </div>
                
                <button
                    onClick={() => setShowFormatModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                >
                    <Info size={16} />
                    Format Fichier
                </button>
            </div>

            {/* Drag & Drop Area - Compact */}
            <div
                className={`relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer group ${
                    dragActive 
                        ? 'border-purple-400 bg-purple-50' 
                        : file 
                            ? 'border-green-400 bg-green-50' 
                            : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept={getFileInputAccept()}
                    className="hidden"
                    disabled={loading}
                />
                
                {file ? (
                    // File Selected State - Horizontal Layout
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                                <FileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-sm truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile();
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <p className="text-gray-600 text-xs">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        
                        {/* Import Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSubmitWithConfirmation();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm flex-shrink-0"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Upload size={16} />
                            )}
                            {loading ? 'Import...' : 'Importer'}
                        </button>
                    </div>
                ) : (
                    // Drop Zone State - Horizontal Layout
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg transition-all flex-shrink-0 ${
                            dragActive ? 'bg-purple-200' : 'bg-gray-100 group-hover:bg-purple-100'
                        }`}>
                            <Layers className={`transition-colors ${
                                dragActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-purple-500'
                            }`} size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                {dragActive ? 'Déposez votre fichier ici' : 'Glissez vos cycles de vie'}
                            </h3>
                            <p className="text-xs text-gray-600">
                                ou <span className="text-purple-600 font-medium">cliquez pour parcourir</span>
                                {' • '}
                                <span className="text-gray-500">CSV, XLSX, XLS</span>
                                {' • '}
                                <span className="text-gray-500">CSV, XLSX</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <AlertTriangle className="text-purple-600" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Confirmer l'import
                            </h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir importer ces données de cycles de vie ? 
                            Cette opération mettra à jour les statuts des produits dans votre base de données.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmedSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                Confirmer l'import
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Format Modal */}
            <FormatModal isOpen={showFormatModal} onClose={() => setShowFormatModal(false)} />

            {/* Upload Result Modal */}
            {uploadResult && (
                <UploadResultModal 
                    isOpen={showResultModal}
                    onClose={handleCloseResultModal}
                    result={uploadResult}
                />
            )}

            {/* Error Messages */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3">
                    <X className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
                    {error}
                </div>
            )}
        </div>
    );
};

export default LifecycleDataUpload;